import axios from 'axios';
import { API_BASE_URL } from '@/lib/apiBase';
import { tokenStore } from '@/lib/tokenStore';

/**
 * Keeping a signed-in session alive - and letting it end - in the browser.
 * The server decides (see docs/AUTH_SESSIONS.md); this side only cooperates:
 *
 * - **One refresh at a time, across tabs.** A refresh rotates the token, so
 *   two tabs refreshing at once would race. A Web Lock queues them, and the
 *   one that waited finds the other's fresh token and uses it instead.
 * - **Refresh ahead, but only for someone who is there.** The access token is
 *   renewed shortly before it expires if the user did something since the
 *   last refresh. A refresh on a timer alone would keep an abandoned session
 *   alive forever and defeat the server's idle timeout.
 * - **All tabs sign in and out together**, over a BroadcastChannel.
 */

/** Renew this long before the access token expires. */
export const REFRESH_AHEAD_MS = 60_000;
/** Warn this long before the session ends. */
export const WARN_BEFORE_MS = 2 * 60_000;

const LOCK_NAME = 'vms-auth-refresh';
const ACTIVITY_KEY = 'vms.last_activity';
const REFRESHED_KEY = 'vms.last_refresh';
/** Activity is written to storage at most this often; every event would thrash it. */
const ACTIVITY_WRITE_MS = 15_000;

// ------------------------------------------------------------------ storage

function readNumber(key) {
  try {
    return Number(window.localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

function writeNumber(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* storage unavailable: this tab still works on its own */
  }
}

// ------------------------------------------------------------- token expiry

/**
 * When a JWT expires, in epoch milliseconds - for scheduling only. Nothing
 * here trusts the token's claims about *who* the user is; /me says that.
 */
export function tokenExpiresAt(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(window.atob(payload));
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------- activity

let lastActivity = 0;

/**
 * Note that the user did something. Shared with other tabs, and throttled -
 * except when `force`d, for a deliberate "I'm here" click.
 */
export function markActivity({ now = Date.now(), force = false } = {}) {
  if (!force && now - lastActivity < ACTIVITY_WRITE_MS) return;
  lastActivity = now;
  writeNumber(ACTIVITY_KEY, now);
}

/** The latest activity in any tab. */
export function lastActivityAt() {
  return Math.max(lastActivity, readNumber(ACTIVITY_KEY));
}

// ------------------------------------------------------------------ refresh

let inFlight = null;

async function refreshUnderLock(staleAccessToken) {
  // Another tab may have refreshed while this one waited for the lock: its
  // token is in storage already, and refreshing again would rotate it away.
  const current = tokenStore.getAccessToken();
  if (
    current &&
    current !== staleAccessToken &&
    (tokenExpiresAt(current) ?? 0) > Date.now() + REFRESH_AHEAD_MS
  ) {
    return current;
  }

  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) throw new Error('no refresh token');

  // Bare axios: apiClient's interceptor would recurse into this function.
  const { data } = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Storefront-Host': window.location.host,
      },
    },
  );
  const tokens = data?.data ?? data;
  // A grace-window answer carries no refresh token; setTokens keeps ours.
  tokenStore.setTokens(tokens);
  writeNumber(REFRESHED_KEY, Date.now());
  return tokens.access_token;
}

/**
 * A fresh access token. `staleAccessToken` is the one that was found wanting,
 * so a token another tab already replaced is recognised as new.
 *
 * One call per tab at a time (shared promise) and one per browser at a time
 * (Web Lock). Browsers without Web Locks fall back to the server's grace
 * window, which is what it is there for.
 */
export function refreshTokens(staleAccessToken = tokenStore.getAccessToken()) {
  if (!inFlight) {
    const run = () => refreshUnderLock(staleAccessToken);
    inFlight = (navigator.locks ? navigator.locks.request(LOCK_NAME, run) : run()).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

// ---------------------------------------------------------------- broadcast

const channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('vms-auth') : null;

/** Tell the other tabs: 'signed-in' or 'signed-out'. Never echoed to this tab. */
export function announce(type) {
  channel?.postMessage({ type });
}

export function onAnnouncement(handler) {
  if (!channel) return () => {};
  const listener = (event) => handler(event.data?.type);
  channel.addEventListener('message', listener);
  return () => channel.removeEventListener('message', listener);
}

/**
 * End the session in this tab and every other: forget the tokens and let the
 * app react (AuthProvider listens). `reason` is 'idle', 'absolute' or 'expired'.
 */
export function endSession(reason = 'expired') {
  tokenStore.clear();
  announce('signed-out');
  window.dispatchEvent(new CustomEvent('auth:session-expired', { detail: { reason } }));
}

// ------------------------------------------------------------------ planner

/**
 * What the session keeper should do right now - a pure function of the
 * clocks, so the rules can be tested without timers or a browser.
 *
 * Returns `{ action, warning, msLeft }`:
 * - action: 'refresh' (renew ahead), 'end-idle' / 'end-absolute', or 'none';
 * - warning: 'idle' (can be extended), 'absolute' (cannot), or null;
 * - msLeft: until the session ends, for the countdown.
 */
export function planSessionTick({
  now,
  accessExpiresAt,
  idleAt,
  absoluteAt,
  lastActivity: activity,
  lastRefresh,
}) {
  const none = { action: 'none', warning: null, msLeft: null };

  if (absoluteAt && now >= absoluteAt) return { ...none, action: 'end-absolute', msLeft: 0 };
  if (idleAt && now >= idleAt) return { ...none, action: 'end-idle', msLeft: 0 };

  const activeSinceRefresh = activity > lastRefresh;
  const accessEndingSoon = accessExpiresAt != null && accessExpiresAt - now <= REFRESH_AHEAD_MS;
  // The idle warning is up (or about to be): activity must push the deadline
  // now, not whenever the access token next runs low - or a user clicking
  // around would watch the countdown carry on.
  const idleEndingSoon = idleAt != null && idleAt - now <= WARN_BEFORE_MS;
  // Refreshing moves the idle deadline; only someone who is there earns that.
  const action = (accessEndingSoon || idleEndingSoon) && activeSinceRefresh ? 'refresh' : 'none';

  // Whichever deadline is nearer is the one to warn about.
  const ends = [idleAt, absoluteAt].filter(Boolean);
  if (ends.length === 0) return { ...none, action };
  const endsAt = Math.min(...ends);
  const msLeft = endsAt - now;
  if (msLeft > WARN_BEFORE_MS) return { action, warning: null, msLeft };

  // About to refresh anyway: the idle deadline is about to move, so no warning.
  const warning = endsAt === absoluteAt ? 'absolute' : action === 'refresh' ? null : 'idle';
  return { action, warning, msLeft };
}

/** The inputs to planSessionTick, read from storage now. */
export function readSessionClocks(now = Date.now()) {
  const access = tokenStore.getAccessToken();
  const { idleAt, absoluteAt } = tokenStore.getSessionDeadlines();
  return {
    now,
    accessExpiresAt: access ? tokenExpiresAt(access) : null,
    idleAt,
    absoluteAt,
    lastActivity: lastActivityAt(),
    lastRefresh: readNumber(REFRESHED_KEY),
  };
}

/** Start the clock at sign-in: no activity since this "refresh" yet. */
export function markSignedIn(now = Date.now()) {
  writeNumber(REFRESHED_KEY, now);
}
